import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { useMediaQuery } from "react-responsive";
import Cookies from "js-cookie";
import API_ENDPOINTS from "../config/api";

const SelectPage = () => {
  const navigate = useNavigate();
  const isDesktop = useMediaQuery({ minWidth: 768 });
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [adminId, setAdminId] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [showClosedModal, setShowClosedModal] = useState(false);

  // 애니메이션 변수
  const pageVariants = {
    initial: {
      opacity: 0,
    },
    in: {
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
    out: {
      opacity: 0,
      transition: {
        duration: 0.3,
      },
    },
  };

  const contentVariants = {
    initial: {
      opacity: 0,
      y: 20,
    },
    in: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: 0.2,
        staggerChildren: 0.1,
      },
    },
  };

  const modalVariants = {
    initial: {
      opacity: 0,
      scale: 0.8,
    },
    animate: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.3,
      },
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      transition: {
        duration: 0.2,
      },
    },
  };

  const handleApply = () => {
    // setShowClosedModal(true);
    // 무조건 identity-verification-input으로 이동
    navigate("/identity-verification-input");
    // navigate("/lawyer-verification");
  };

  const handleCheck = () => {
    navigate("/check-status");
  };

  const handleKakaoOpen = () => {
    window.open(
      "https://open.kakao.com/o/gRAHORth?utm_source=SKT&utm_medium=web_info&utm_campaign=web_info_last",
      "_blank"
    );
  };

  const handleAdminLogin = () => {
    setShowAdminModal(true);
  };

  const closeAdminModal = () => {
    setShowAdminModal(false);
    setAdminId("");
    setAdminPassword("");
    setLoginError("");
  };

  const handleClosedModalConfirm = () => {
    setShowClosedModal(false);
  };

  const submitAdminLogin = async () => {
    if (!adminId || !adminPassword) {
      setLoginError("아이디와 비밀번호를 모두 입력해주세요.");
      return;
    }

    setIsLoggingIn(true);
    setLoginError("");

    try {
      const response = await fetch(
        `${API_ENDPOINTS.BASE_URL}/auth/test-login`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            id: adminId,
            password: adminPassword,
          }),
        }
      );

      const data = await response.json();

      if (response.status === 201 && data.verified) {
        // 토큰을 쿠키에 저장
        Cookies.set(API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN, data.token, {
          expires: API_ENDPOINTS.COOKIE_CONFIG.EXPIRES.ACCESS_TOKEN,
          ...API_ENDPOINTS.COOKIE_CONFIG.OPTIONS,
        });

        // 전화번호도 쿠키에 저장
        if (data.phoneNumber) {
          Cookies.set("verifiedPhoneNumber", data.phoneNumber, {
            expires: 7,
            ...API_ENDPOINTS.COOKIE_CONFIG.OPTIONS,
          });
        }

        // 모달 닫기
        closeAdminModal();

        // 결제 방법 선택 페이지로 바로 이동
        navigate("/payment-methods");
      } else {
        setLoginError(
          "로그인에 실패했습니다. 아이디와 비밀번호를 확인해주세요."
        );
      }
    } catch (error) {
      setLoginError("로그인 중 오류가 발생했습니다. 나중에 다시 시도해주세요.");
    } finally {
      setIsLoggingIn(false);
    }
  };

  return (
    <PageBackground>
      <Container
        variants={pageVariants}
        initial="initial"
        animate="in"
        exit="out"
      >
        <BackgroundImage />
        <Content variants={contentVariants} initial="initial" animate="in">
          <ButtonsContainer>
            <ApplyButton
              onClick={handleApply}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              집단소송 접수하기
              <ArrowIcon src="/png/arrowWhite.png" alt="화살표" />
            </ApplyButton>

            <CheckButton
              onClick={handleCheck}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              신청내역 조회하기
              <ArrowIcon src="/png/arrowBlue.png" alt="화살표" />
            </CheckButton>
          </ButtonsContainer>

          {/* 관리자 로그인 버튼 */}
          {/* <AdminLoginButton onClick={handleAdminLogin}>
            테스트 관리자
          </AdminLoginButton> */}
        </Content>
      </Container>

      {/* 관리자 로그인 모달 */}
      <AnimatePresence>
        {showAdminModal && (
          <ModalOverlay>
            <ModalContainer
              variants={modalVariants}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <ModalHeader>
                <ModalTitle>서버 관리자 로그인</ModalTitle>
              </ModalHeader>
              <ModalContent>
                <InputGroup>
                  <InputLabel>아이디</InputLabel>
                  <Input
                    type="text"
                    value={adminId}
                    onChange={(e) => setAdminId(e.target.value)}
                    placeholder="관리자 아이디 입력"
                  />
                </InputGroup>
                <InputGroup>
                  <InputLabel>비밀번호</InputLabel>
                  <Input
                    type="password"
                    value={adminPassword}
                    onChange={(e) => setAdminPassword(e.target.value)}
                    placeholder="비밀번호 입력"
                  />
                </InputGroup>
                {loginError && <ErrorMessage>{loginError}</ErrorMessage>}
              </ModalContent>
              <ModalFooter>
                <ButtonGroup>
                  <CancelButton onClick={closeAdminModal}>취소</CancelButton>
                  <LoginButton
                    onClick={submitAdminLogin}
                    disabled={isLoggingIn}
                  >
                    {isLoggingIn ? "로그인 중..." : "로그인"}
                  </LoginButton>
                </ButtonGroup>
              </ModalFooter>
            </ModalContainer>
          </ModalOverlay>
        )}
      </AnimatePresence>
      {/* 소장 접수 마감 안내 모달 */}
      <AnimatePresence>
        {showClosedModal && (
          <ModalOverlay>
            <ModalContainer
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
            >
              <ModalTitle>소장 접수가 마감되었습니다</ModalTitle>
              <ModalContent>
                해당 집단소송의 소장 접수 기간이 종료되어,
                <br />
                현재는 신규 참여가 불가능합니다.
              </ModalContent>
              <ModalButtonGroup>
                <ModalConfirmButton
                  onClick={handleClosedModalConfirm}
                  style={{ width: "100%" }}
                >
                  확인
                </ModalConfirmButton>
              </ModalButtonGroup>
            </ModalContainer>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </PageBackground>
  );
};

// 스타일 컴포넌트
const PageBackground = styled.div`
  width: 100%;
  min-height: 100vh;
  background-color: #f6f8ff;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  position: relative;

  @media (min-width: 769px) {
    padding: 20px;
  }
`;

const Container = styled(motion.div)`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  /* 모바일 디바이스 */
  @media (max-width: 768px) {
    min-height: 100vh;
    box-shadow: none;
    background-color: transparent;
    padding: 0;
    max-width: 100%;
    margin: 0;
    border-radius: 0;
  }

  /* 데스크탑 디바이스 */
  @media (min-width: 769px) {
    background-color: #f6f8ff;
    border-radius: 20px;
    height: 700px;
    min-height: 700px;
    max-width: 470px;
    margin: 0 auto;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.05);
    padding: 20px;
  }
`;

const BackgroundImage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url("/png/bg2.png");
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  z-index: 0;

  @media (max-width: 768px) {
    border-radius: 0;
  }

  @media (min-width: 769px) {
    border-radius: 20px;
    top: 20px;
    left: 20px;
    width: calc(100% - 40px);
    height: calc(100% - 40px);
  }
`;

const Content = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: left;
  z-index: 2;
  width: 100%;
  height: 90%;
  position: relative;

  @media (max-width: 768px) {
    padding: 0 20px;
    min-height: 100vh;
  }

  @media (min-width: 769px) {
    min-height: 700px;
    padding: 60px 20px;
  }
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  margin-top: 30px;
`;

const TitleImage = styled.img`
  width: 100%;
  max-width: 320px;
`;

const SubTitle = styled.p`
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  word-break: keep-all;
  color: #666;
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  width: 100%;
  max-width: 400px;
  position: absolute;
  top: 550px;
  left: 50%;
  transform: translateX(-50%);
  padding: 0 20px;

  @media (min-width: 769px) {
    bottom: 100px;
  }
`;

const ApplyButton = styled(motion.button)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 56px;
  height: 56px;
  padding: 0 20px;
  border-radius: 20px;
  background: #3855e2;
  color: white;
  font-size: 16px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  box-sizing: border-box;

  @media (min-width: 769px) {
    min-height: 56px;
    height: 56px;
  }
`;

const CheckButton = styled(motion.button)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 56px;
  height: 56px;
  padding: 0 20px;
  border-radius: 20px;
  background: white;
  color: #3855e2;
  font-size: 16px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  box-sizing: border-box;

  @media (min-width: 769px) {
    min-height: 56px;
    height: 56px;
  }
`;

const ArrowIcon = styled.img`
  width: 16px;
  height: 16px;
  object-fit: contain;
`;

// 관리자 로그인 버튼
const AdminLoginButton = styled.button`
  font-size: 12px;
  color: #999;
  background: none;
  border: none;
  margin-top: -30px;
  cursor: pointer;
  padding: 8px 16px;
  border-radius: 4px;

  &:hover {
    text-decoration: underline;
  }
`;

// 모달 관련 스타일
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContainer = styled(motion.div)`
  width: 90%;
  max-width: 400px;
  background-color: white;
  border-radius: 16px;
  overflow: hidden;
  padding: 32px 24px 24px 24px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
`;

const ModalHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid #eee;
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 18px 0;
  color: #333;
`;

const ModalContent = styled.div`
  padding: 0 0 28px 0;
  font-size: 16px;
  color: #333;
  text-align: left;
  line-height: 1.6;
`;

const ModalFooter = styled.div`
  padding: 15px 20px;
  border-top: 1px solid #eee;
`;

const InputGroup = styled.div`
  margin-bottom: 16px;
`;

const InputLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  height: 46px;
  padding: 0 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 15px;

  &:focus {
    outline: none;
    border-color: #0075ff;
  }
`;

const ErrorMessage = styled.p`
  color: #ff4c4c;
  font-size: 14px;
  margin-top: 8px;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const CancelButton = styled.button`
  height: 40px;
  padding: 0 16px;
  background-color: #f5f5f5;
  color: #333;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
`;

const LoginButton = styled.button`
  height: 40px;
  padding: 0 16px;
  background-color: #0075ff;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  opacity: ${(props) => (props.disabled ? 0.7 : 1)};
  pointer-events: ${(props) => (props.disabled ? "none" : "auto")};
`;

// 모달 버튼 그룹 및 확인 버튼 스타일 추가
const ModalButtonGroup = styled.div`
  display: flex;
  justify-content: center;
  padding: 0;
  margin-top: 8px;
`;
const ModalConfirmButton = styled.button`
  height: 44px;
  width: 100%;
  background-color: #0075ff;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
`;

export default SelectPage;
